import React, { useEffect, useMemo, useState } from 'react';
import fetchData from '../../api/fetchApi';

/**
 * Reusable categories list component.
 * - Fetches categories from GET admin/law-categories unless `categories` prop is provided.
 * - Renders selectable chips and calls onChange with the selected category uuid/id.
 */
export default function CategoriesList({
  value,
  onChange,
  categories: categoriesProp,
  disabled = false,
  className = '',
  buttonClassName = '',
  selectedClassName = 'bg-[#8C1C13] text-white border-[#8C1C13]',
  unselectedClassName = 'bg-white text-gray-700 border-gray-300',
  loadingText = 'جارٍ التحميل...'
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const parseItems = (res) => {
    const raw = Array.isArray(res) ? res : (res?.items || res?.data || res?.categories || []);
    return Array.isArray(raw) ? raw : [];
  };

  useEffect(() => {
    let mounted = true;
    if (categoriesProp && Array.isArray(categoriesProp)) {
      setItems(categoriesProp);
      return;
    }
    setLoading(true);
    fetchData('admin/law-categories')
      .then((res) => {
        if (!mounted) return;
        setItems(parseItems(res));
      })
      .catch((e) => {
        if (!mounted) return;
        setError(e);
        setItems([]);
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [categoriesProp]);

  const content = useMemo(() => {
    if (loading) return <span className="text-sm text-gray-500">{loadingText}</span>;
    if (error) return <span className="text-sm text-red-600">فشل تحميل التصنيفات</span>;
    if (!items.length) return <span className="text-sm text-gray-500">لا توجد تصنيفات متاحة</span>;

    return items.map((cat) => {
      const key = cat.uuid || cat.id || (cat.name_ar || cat.name || cat.title);
      const name = cat.name_ar || cat.name || cat.title || 'غير مسمى';
      const id = cat.uuid || cat.id || '';
      const selected = value && value === id;
      return (
        <button
          key={key}
          type="button"
          disabled={disabled}
          className={`px-4 py-2 rounded-lg border text-sm font-medium ${selected ? selectedClassName : unselectedClassName} ${disabled ? 'opacity-60 cursor-not-allowed' : ''} ${buttonClassName}`}
          onClick={() => !disabled && onChange && onChange(id)}
        >
          {name}
        </button>
      );
    });
  }, [items, value, onChange, disabled, loading, error, buttonClassName, selectedClassName, unselectedClassName, loadingText]);

  return (
    <div className={`flex flex-wrap gap-3 ${className}`}>
      {content}
    </div>
  );
}
